import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useRazorpay } from 'react-razorpay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get('type') || 'physical_card';
  
  const { Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: ''
  });
  const [amount, setAmount] = useState(1000);

  useEffect(() => {
    setAmount(orderType === 'physical_card' ? 1000 : 365);
  }, [orderType]);

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (orderType === 'physical_card' && (!shippingInfo.name || !shippingInfo.address || !shippingInfo.pincode)) {
      toast.error('Please fill in all shipping details');
      return;
    }

    setLoading(true);

    try {
      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/orders/create`,
        {
          order_type: orderType,
          amount: amount,
          shipping_name: shippingInfo.name,
          shipping_address: shippingInfo.address,
          shipping_city: shippingInfo.city,
          shipping_state: shippingInfo.state,
          shipping_pincode: shippingInfo.pincode,
          shipping_phone: shippingInfo.phone
        },
        { withCredentials: true }
      );

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount * 100,
        currency: orderResponse.data.currency,
        name: 'Sol Digital',
        description: orderType === 'physical_card' ? 'Physical NFC Business Card' : 'Annual Subscription',
        order_id: orderResponse.data.razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${BACKEND_URL}/api/orders/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            toast.success('Payment successful!');
            navigate('/dashboard/orders');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: shippingInfo.name,
          contact: shippingInfo.phone
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA] py-12 px-4" data-testid="checkout-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold text-[#0A0A0A] mb-2">Checkout</h1>
          <p className="text-[#525252]">
            {orderType === 'physical_card' 
              ? 'Complete your purchase of Physical NFC Card' 
              : 'Renew your annual subscription'}
          </p>
        </div>

        <div className="bg-white backdrop-blur-xl border border-[#0A0A0A]/10 rounded-2xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#0A0A0A]/10">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-[#B8935A]" />
              <div>
                <h3 className="text-lg font-semibold text-[#0A0A0A]">
                  {orderType === 'physical_card' ? 'Physical NFC Card' : 'Annual Subscription'}
                </h3>
                <p className="text-sm text-[#525252]">
                  {orderType === 'physical_card' 
                    ? 'Includes 1 year subscription' 
                    : 'Keep your profile active'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-heading font-bold text-[#0A0A0A]">₹{amount}</p>
              <p className="text-sm text-[#525252]">
                {orderType === 'physical_card' ? 'one-time' : 'per year'}
              </p>
            </div>
          </div>

          {orderType === 'physical_card' && (
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <Label className="text-[#0A0A0A] mb-2 block">Full Name *</Label>
                <Input
                  value={shippingInfo.name}
                  onChange={(e) => setShippingInfo({ ...shippingInfo, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A]"
                  data-testid="shipping-name-input"
                  required
                />
              </div>

              <div>
                <Label className="text-[#0A0A0A] mb-2 block">Phone Number *</Label>
                <Input
                  type="tel"
                  value={shippingInfo.phone}
                  onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A]"
                  data-testid="shipping-phone-input"
                  required
                />
              </div>

              <div>
                <Label className="text-[#0A0A0A] mb-2 block">Address *</Label>
                <Input
                  value={shippingInfo.address}
                  onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                  placeholder="Street address, apartment, suite, etc."
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A]"
                  data-testid="shipping-address-input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#0A0A0A] mb-2 block">City *</Label>
                  <Input
                    value={shippingInfo.city}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                    placeholder="Mumbai"
                    className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A]"
                    data-testid="shipping-city-input"
                    required
                  />
                </div>

                <div>
                  <Label className="text-[#0A0A0A] mb-2 block">State *</Label>
                  <Input
                    value={shippingInfo.state}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                    placeholder="Maharashtra"
                    className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A]"
                    data-testid="shipping-state-input"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#0A0A0A] mb-2 block">PIN Code *</Label>
                <Input
                  value={shippingInfo.pincode}
                  onChange={(e) => setShippingInfo({ ...shippingInfo, pincode: e.target.value })}
                  placeholder="400001"
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A]"
                  data-testid="shipping-pincode-input"
                  maxLength={6}
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0A0A0A] text-white rounded-full h-14 text-lg font-medium hover:bg-[#1a1a1a] transition-all active:scale-95 shadow-lg shadow-blue-500/20 mt-6"
                data-testid="proceed-to-payment-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Pay ₹{amount}</>
                )}
              </Button>
            </form>
          )}

          {orderType === 'subscription' && (
            <Button
              onClick={handlePayment}
              disabled={loading}
              className="w-full bg-[#0A0A0A] text-white rounded-full h-14 text-lg font-medium hover:bg-[#1a1a1a] transition-all active:scale-95 shadow-lg shadow-blue-500/20"
              data-testid="proceed-to-payment-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Pay ₹{amount}</>
              )}
            </Button>
          )}
        </div>

        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
          <h3 className="text-[#0A0A0A] font-semibold mb-3">Secure Payment</h3>
          <p className="text-sm text-[#525252]">
            Your payment is processed securely through Razorpay. We support UPI, Cards, Net Banking, and Wallets.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
